// Enquiry detail (Session ARE, Director decision D82): the office's one screen for reviewing an
// AI-drafted reply and sending it, matching the enquiry to a customer, raising real work from it,
// and deleting it if it was raised in error. Read EnquiriesPage.tsx first for how a row gets here.
//
// This is the record page the office-UI restructure audit's Phase 2 direction calls for
// (docs/reports/OFFICE-UI-RESTRUCTURE-AUDIT-2026-09-23.md): RecordHeader for the title/status/back
// link, RecordLink/RecordLinkGroup for the cross-links to what this converted into, AuditTimeline
// fed from the generic GET /api/audit-log?entity=enquiry&entityId= endpoint every other record page
// already uses. Mirrors ComplaintDetailPage.tsx's and CalloutHubPage.tsx's own load/state shape.
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { AuditTimelineEntry, EmailEngineMessageRow, EnquiryDetail, EnquiryFactProposal } from "../../lib/api";
import { sites as sitesResource } from "../../lib/resources";
import type { Site } from "../../lib/resources";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { RecordHeader } from "../../components/RecordHeader";
import { RecordPicker } from "../../components/RecordPicker";
import { RecordLinkGroup, RecordLink } from "../../components/RecordLink";
import { StatusPill } from "../../components/StatusPill";
import { Button } from "../../components/Button";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { AuditTimeline } from "../../components/AuditTimeline";
import { AcceptCallAsWorkModal, type RaiseWorkSource } from "./AcceptCallAsWorkModal";
import { RaiseWorkModal, raisedWorkMessage } from "./RaiseWorkModal";
import { EnquirySendConfirmModal } from "./EnquirySendConfirmModal";
import { outcomeDisplay } from "./emailEngineUi";
import { formatDateTime } from "../../lib/format";
import { Loader } from "../../components/Loader";
import { statusEntry } from "../../lib/statusRegistry";
import {
  ENQUIRY_STATUS_LABEL,
  DRAFT_TIER_LABEL,
  enquiryDraftSource,
  FACT_TYPE_LABEL,
  counterpartyLine,
  gmailThreadHref,
} from "./enquiriesUi";

const inputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-70";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const sectionCls = "mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5";

export function EnquiryDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [enquiry, setEnquiry] = useState<EnquiryDetail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setState("loading");
      try {
        const res = await api.enquiries.get(id);
        setEnquiry(res.enquiry);
        setState("ready");
      } catch (err) {
        if (!opts?.silent) setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
      }
    },
    [id],
  );
  const refresh = useCallback(() => load({ silent: true }), [load]);
  useEffect(() => {
    void load();
  }, [load]);

  if (state === "loading") return <Loader shape="panel" className="p-6" />;
  if (state === "notfound") return <p className="p-6 text-center text-ele-body">That enquiry could not be found.</p>;
  if (state === "error" || !enquiry) return <p role="alert" className="p-6 text-center text-ele-strong">Could not load this enquiry. Please try again.</p>;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <RecordHeader
        title={enquiry.subject?.trim() || "(no subject)"}
        backTo="/office/mail?tab=reply"
        backLabel="Back to Mail"
        status={<StatusPill word={ENQUIRY_STATUS_LABEL[enquiry.status]} tone={statusEntry("enquiry", enquiry.status).tone} />}
        subtitle={`${counterpartyLine(enquiry)} · Last message ${formatDateTime(enquiry.lastMessageAt)}`}
        className="mb-4"
      />

      <ThreadSection enquiry={enquiry} />
      <EmailEngineSection enquiryId={enquiry.id} />
      <MatchSection enquiry={enquiry} onChanged={refresh} />
      <DraftSection enquiry={enquiry} onChanged={refresh} />
      <SuggestedUpdatesSection enquiry={enquiry} />
      <RaiseWorkSection enquiry={enquiry} onChanged={refresh} />

      <section className="mb-4 rounded-ele border border-ele-hairline bg-ele-surface p-4">
        <h2 className="font-heading text-lg tracking-wide text-ele-strong">Activity</h2>
        <div className="mt-3">
          <ActivitySection entityId={enquiry.id} />
        </div>
      </section>

      <DeleteSection enquiry={enquiry} onDeleted={() => navigate("/office/mail?tab=reply")} />
    </div>
  );
}

function ThreadSection({ enquiry }: { enquiry: EnquiryDetail }) {
  return (
    <section className={sectionCls}>
      <h2 className="mb-3 font-heading text-lg text-ele-strong">The email</h2>
      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <dt className={labelCls}>From</dt>
          <dd className="mt-0.5 text-ele-strong">{counterpartyLine(enquiry)}</dd>
        </div>
        <div>
          <dt className={labelCls}>Mailbox</dt>
          <dd className="mt-0.5 text-ele-strong">{enquiry.mailbox}</dd>
        </div>
        <div>
          <dt className={labelCls}>Last message</dt>
          <dd className="mt-0.5 text-ele-strong">
            {formatDateTime(enquiry.lastMessageAt)} ({enquiry.lastMessageDirection === "inbound" ? "from them" : "from ELE"})
          </dd>
        </div>
        <div>
          <dt className={labelCls}>Gmail thread</dt>
          <dd className="mt-0.5">
            <a
              href={gmailThreadHref(enquiry.gmailThreadId)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-ele-blue underline-offset-2 hover:underline"
            >
              Open in Gmail
            </a>
          </dd>
        </div>
      </dl>
      {enquiry.lastMessageSnippet && (
        <p className="mt-3 rounded-ele border border-ele-hairline bg-ele-inset px-3 py-2 text-sm text-ele-body">{enquiry.lastMessageSnippet}</p>
      )}
      {enquiry.contentReason && (
        <p className="mt-3 text-xs italic text-ele-body opacity-80">
          Why this needs a reply: {enquiry.contentHighConfidence ? "high confidence. " : ""}
          {enquiry.contentReason}
        </p>
      )}
    </section>
  );
}

// Session ASO: the email engine's record of this enquiry's inbound messages, each with its outcome and reason, and a
// link to the message on the Email engine page (its draft, checks and facts). Read-only here: nothing else on this page
// changes. A failed read says so rather than showing an empty list that looks complete.
function EmailEngineSection({ enquiryId }: { enquiryId: string }) {
  const [rows, setRows] = useState<EmailEngineMessageRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setRows(null);
    setError(null);
    Promise.resolve()
      .then(() => api.emailEngine.messages({ enquiryId, limit: 50 }))
      .then((res) => {
        if (live) setRows(res.messages);
      })
      .catch((err) => {
        if (live) setError(friendlyApiError(err, "The email engine's record of this enquiry could not be loaded."));
      });
    return () => {
      live = false;
    };
  }, [enquiryId]);

  return (
    <section className={sectionCls} aria-label="Email engine">
      <h2 className="mb-1 font-heading text-lg text-ele-strong">Email engine</h2>
      <p className="mb-3 text-xs text-ele-body">Each message in this conversation, and what the email engine decided about it.</p>
      {error ? (
        <p role="alert" className="text-sm text-ele-strong">
          {error}
        </p>
      ) : rows === null ? (
        <Loader shape="inline" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-ele-body">The email engine has not recorded any message for this enquiry.</p>
      ) : (
        <ul className="divide-y divide-ele-hairline text-sm">
          {rows.map((m) => {
            const pill = outcomeDisplay(m);
            return (
              <li key={m.id} data-testid={`engine-message-${m.id}`} className="flex flex-col gap-1 py-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusPill word={pill.word} tone={pill.tone} />
                    <span className="text-xs text-ele-body">{formatDateTime(m.receivedAt)}</span>
                  </div>
                  {m.outcomeReason && <p className="mt-1 text-ele-body">{m.outcomeReason}</p>}
                </div>
                <Link to={`/office/email-engine/${encodeURIComponent(m.id)}`} className="shrink-0 text-ele-blue underline-offset-2 hover:underline">
                  Open
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function MatchSection({ enquiry, onChanged }: { enquiry: EnquiryDetail; onChanged: () => Promise<void> }) {
  const [editing, setEditing] = useState(!enquiry.customerId);
  const [sites, setSites] = useState<Site[]>([]);
  const [sitesLoaded, setSitesLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resync when a DIFFERENT enquiry loads, or when this one's own match changes underneath it (a
  // successful match closes the picker automatically rather than leaving it open on a stale state).
  useEffect(() => {
    setEditing(!enquiry.customerId);
  }, [enquiry.id, enquiry.customerId]);

  useEffect(() => {
    if (!enquiry.customerId) {
      setSites([]);
      setSitesLoaded(false);
      return;
    }
    let live = true;
    setSitesLoaded(false);
    sitesResource
      .list({ customerId: enquiry.customerId })
      .then((rows) => {
        if (!live) return;
        setSites(rows.filter((s) => s.customerId === enquiry.customerId));
        setSitesLoaded(true);
      })
      .catch(() => {
        if (live) setSitesLoaded(true);
      });
    return () => {
      live = false;
    };
  }, [enquiry.customerId]);


  async function setCustomer(customerId: string) {
    setSaving(true);
    setError(null);
    try {
      await api.enquiries.match(enquiry.id, { customerId, siteId: null });
      await onChanged();
    } catch (err) {
      setError(friendlyApiError(err, "Could not save the match. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  async function setSite(siteId: string) {
    setSaving(true);
    setError(null);
    try {
      await api.enquiries.match(enquiry.id, { customerId: enquiry.customerId, siteId: siteId || null });
      await onChanged();
    } catch (err) {
      setError(friendlyApiError(err, "Could not save the site. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={sectionCls}>
      <h2 className="mb-3 font-heading text-lg text-ele-strong">Who this is from</h2>

      {!editing && enquiry.customerId ? (
        <div className="flex flex-wrap items-center gap-3">
          <Link to={`/office/customers/${encodeURIComponent(enquiry.customerId)}`} className="text-ele-green underline-offset-2 hover:underline">
            {enquiry.customerName ?? "View customer"}
          </Link>
          <button type="button" onClick={() => setEditing(true)} className="font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong">
            Change
          </button>
        </div>
      ) : (
        <div>
          <StatusPill word="Not matched to a customer" tone="grey" />
          <p className="mt-2 text-sm text-ele-body">Search for the customer this email is from.</p>
          <div className="mt-2 sm:max-w-sm">
            <RecordPicker
              kind="customer"
              value=""
              label="Search customers by name"
              placeholder="Search customers by name"
              disabled={saving}
              onChange={(id) => {
                if (id) void setCustomer(id);
              }}
            />
          </div>
          {enquiry.customerId && (
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="mt-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {enquiry.customerId && !editing && (
        <div className="mt-3">
          <label className={labelCls} htmlFor="enquiry-site">
            Site (optional)
          </label>
          <select
            id="enquiry-site"
            value={enquiry.siteId ?? ""}
            onChange={(e) => void setSite(e.target.value)}
            disabled={saving || !sitesLoaded}
            className={`${inputCls} sm:max-w-sm`}
          >
            <option value="">No specific site</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {sitesLoaded && sites.length === 0 && <p className="mt-1 text-xs text-ele-body opacity-70">This customer has no sites on the register.</p>}
        </div>
      )}

      {error && (
        <p role="alert" className="mt-3 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

// An HTML email body, previewed in an isolated iframe rather than injected into this page's own DOM
// (no dangerouslySetInnerHTML anywhere in this app, and no HTML sanitiser is installed to make that
// safe -- checked before choosing this). `sandbox=""` is the empty, maximally-restrictive sandbox:
// no script execution, no same-origin access, no forms, no top-level navigation. The content is an
// AI-drafted reply the office has not sent yet, so it is treated as untrusted until approved.
function SafeHtmlPreview({ html }: { html: string }) {
  return (
    <iframe
      title="Reply preview"
      srcDoc={html}
      sandbox=""
      className="h-72 w-full rounded-ele border border-ele-hairline bg-white"
    />
  );
}

function DraftSection({ enquiry, onChanged }: { enquiry: EnquiryDetail; onChanged: () => Promise<void> }) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editSubject, setEditSubject] = useState(enquiry.draftContent?.subject ?? "");
  const [editBody, setEditBody] = useState(enquiry.draftContent?.bodyHtml ?? "");
  const [dirty, setDirty] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmingSend, setConfirmingSend] = useState(false);

  useEffect(() => {
    setEditSubject(enquiry.draftContent?.subject ?? "");
    setEditBody(enquiry.draftContent?.bodyHtml ?? "");
    setDirty(false);
    setSaved(false);
  }, [enquiry.id, enquiry.draftContent?.subject, enquiry.draftContent?.bodyHtml]);

  // The one point in the brief this page exists to serve: a reviewed draft goes out in one press.
  // Not offered at all for a novel-tier draft (see the tier check below) -- that is the review step
  // for THIS kind of enquiry, and it happens in Gmail, not here.
  const editable = enquiry.status === "awaiting_review";
  const canSend = enquiry.status === "awaiting_review" || enquiry.status === "approved";
  const isNovel = enquiry.draftTier === "novel";

  async function generate() {
    setGenerating(true);
    setError(null);
    try {
      await api.enquiries.generateDraft(enquiry.id);
      await onChanged();
    } catch (err) {
      setError(friendlyApiError(err, "Could not generate a draft. Please try again."));
    } finally {
      setGenerating(false);
    }
  }

  async function saveDraft() {
    setSavingDraft(true);
    setError(null);
    try {
      await api.enquiries.updateDraft(enquiry.id, { subject: editSubject, bodyHtml: editBody });
      setDirty(false);
      setSaved(true);
      await onChanged();
    } catch (err) {
      setError(friendlyApiError(err, "Could not save your changes. Please try again."));
    } finally {
      setSavingDraft(false);
    }
  }

  // Session ATW: the send goes through a branded confirm that names the real recipient (whoever wrote, read from the
  // conversation) and passes it back, so the reply never reaches someone the office was not shown.
  async function approveAndSend(expectedTo: string | undefined) {
    setSending(true);
    setError(null);
    try {
      await api.enquiries.approveAndSend(enquiry.id, expectedTo);
      setConfirmingSend(false);
      await onChanged();
    } catch (err) {
      setError(friendlyApiError(err, "Could not send the reply. Please try again."));
    } finally {
      setSending(false);
    }
  }

  return (
    <section className={sectionCls}>
      <h2 className="mb-3 font-heading text-lg text-ele-strong">The reply</h2>

      {enquiry.status === "sent" ? (
        <div className="rounded-ele border border-ele-green bg-ele-inset px-3 py-2">
          <StatusPill word="Sent" tone="green" />
          <p className="mt-2 text-sm text-ele-strong">{enquiry.sentAt ? `Sent ${formatDateTime(enquiry.sentAt)}.` : "Sent."}</p>
        </div>
      ) : !enquiry.draftContent ? (
        isNovel ? (
          <div>
            <p className="mb-1 text-sm text-ele-strong">This one needs a reply written by hand.</p>
            <p className="text-sm text-ele-body">
              No automatic reply was drafted for this enquiry. Open &quot;Enquiry draft generated&quot; in the Activity section below and press{" "}
              &quot;Show detail&quot; to read the AI&apos;s summary of what it found, then write and send the reply yourself in Gmail.
            </p>
          </div>
        ) : (
          <div>
            <p className="text-sm text-ele-body">No reply has been drafted yet.</p>
            <p className="mb-3 text-xs text-ele-body opacity-80">
              Draft a reply with the email engine. It uses only facts from the platform&apos;s records and checks every figure before
              offering it.
            </p>
            <Button size="sm" onClick={() => void generate()} disabled={generating}>
              {generating ? "Drafting..." : "Generate draft"}
            </Button>
          </div>
        )
      ) : (
        <div>
          <p className="mb-2 text-xs text-ele-body opacity-70">
            {enquiry.draftTier ? DRAFT_TIER_LABEL[enquiry.draftTier] : "Drafted."}
            {enquiry.draftGeneratedAt ? ` Generated ${formatDateTime(enquiry.draftGeneratedAt)}.` : ""}
            {enquiry.draftModel ? ` (${enquiry.draftModel})` : ""}
          </p>
          {enquiryDraftSource(enquiry) && (
            <p className="mb-2 text-xs text-ele-body" data-testid="enquiry-draft-source">
              {enquiryDraftSource(enquiry)}. Signature: Company signature.
            </p>
          )}

          {isNovel && (
            <p className="mb-3 rounded-ele border border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-[var(--ele-amber-text)]">
              The email engine could not offer a draft that passed every check. Read the summary below, then write and send the reply yourself in Gmail.
            </p>
          )}

          <label className={labelCls} htmlFor="draft-subject">
            Subject
          </label>
          <input
            id="draft-subject"
            value={editSubject}
            readOnly
            disabled
            className={`${inputCls} mb-1`}
          />
          <p className="mb-3 text-xs text-ele-body opacity-70">
            A reply keeps the conversation&apos;s own subject so it stays in the same email thread.
          </p>

          <label className={labelCls} htmlFor="draft-body">
            Message
          </label>
          <SafeHtmlPreview html={editBody} />
          {editable && (
            <>
              <textarea
                id="draft-body"
                aria-label="Message (HTML)"
                value={editBody}
                onChange={(e) => {
                  setEditBody(e.target.value);
                  setDirty(true);
                  setSaved(false);
                }}
                disabled={savingDraft}
                rows={8}
                className={`${inputCls} mt-2`}
              />
              <p className="mt-1 text-xs text-ele-body opacity-70">
                Edited as the underlying HTML. The box above previews the wording; it is sent as a plain email reply in the
                conversation, with the Company signature added.
              </p>
            </>
          )}

          {editable && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button variant="secondary" size="sm" onClick={() => void saveDraft()} disabled={!dirty || savingDraft}>
                {savingDraft ? "Saving..." : "Save changes"}
              </Button>
              {saved && !dirty && (
                <span role="status" className="text-xs text-ele-green">
                  Saved.
                </span>
              )}
            </div>
          )}

          {!isNovel && canSend && (
            <div className="mt-4 border-t border-ele-hairline pt-4">
              <Button onClick={() => setConfirmingSend(true)} disabled={sending || dirty}>
                {sending ? "Sending..." : "Approve and send"}
              </Button>
              {dirty && <p className="mt-1 text-xs text-ele-body opacity-70">Save your changes first.</p>}
            </div>
          )}
        </div>
      )}

      {error && !confirmingSend && (
        <p role="alert" className="mt-3 text-sm text-ele-danger">
          {error}
        </p>
      )}
      {confirmingSend && (
        <EnquirySendConfirmModal
          targets={[{ id: enquiry.id, label: enquiry.subject?.trim() || enquiry.counterpartyEmail || "This enquiry" }]}
          busy={sending}
          error={error}
          onConfirm={(recipients) => void approveAndSend(recipients.get(enquiry.id))}
          onCancel={() => {
            if (sending) return;
            setConfirmingSend(false);
            setError(null);
          }}
        />
      )}
    </section>
  );
}

function RaiseWorkSection({ enquiry, onChanged }: { enquiry: EnquiryDetail; onChanged: () => Promise<void> }) {
  const navigate = useNavigate();
  const [accepting, setAccepting] = useState(false);
  const [raising, setRaising] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const hasConverted = enquiry.convertedJobs.length > 0 || enquiry.convertedCallOuts.length > 0;

  if (!enquiry.customerId) {
    return (
      <section className={sectionCls}>
        <h2 className="mb-2 font-heading text-lg text-ele-strong">Raise work</h2>
        <p className="text-sm text-ele-body">Match this enquiry to a customer above before raising work from it.</p>
      </section>
    );
  }
  // Narrowed once, into a plain string const, so it stays narrowed inside the closures below (a
  // property access on `enquiry` itself would not: TS re-widens it through a nested function).
  const customerId = enquiry.customerId;
  const customerName = enquiry.customerName;

  const raiseSource: RaiseWorkSource = {
    id: enquiry.id,
    customerId,
    customerName,
    // An enquiry has no phone number; the caller name it can offer is the counterparty's.
    callerName: enquiry.counterpartyName,
    callerPhone: null,
    topic: enquiry.subject,
  };

  return (
    <section className={sectionCls}>
      <h2 className="mb-2 font-heading text-lg text-ele-strong">Raise work</h2>

      {hasConverted ? (
        <RecordLinkGroup title="Converted into" empty="Not converted into work yet.">
          {enquiry.convertedJobs.map((j) => (
            <RecordLink key={j.id} to={`/office/jobsheets/${encodeURIComponent(j.id)}`} label={j.ref ?? j.id} meta={j.type} />
          ))}
          {enquiry.convertedCallOuts.map((c) => (
            <RecordLink key={c.id} to={`/office/callouts/${encodeURIComponent(c.id)}`} label={c.ref ?? c.id} meta="Call-out" />
          ))}
        </RecordLinkGroup>
      ) : (
        <>
          <p className="mb-3 text-sm text-ele-body">Is this enquiry real work? Choose what kind, or leave it as an email reply only.</p>
          <Button size="sm" onClick={() => setAccepting(true)}>
            Raise work from this enquiry
          </Button>
        </>
      )}

      {notice && (
        <p role="status" className="mt-3 rounded-ele border border-ele-green bg-ele-field-inset px-3 py-2 text-sm text-ele-strong">
          {notice}
        </p>
      )}

      {accepting && (
        <AcceptCallAsWorkModal
          call={raiseSource}
          onClose={() => setAccepting(false)}
          onCallout={() => {
            // Mirrors CustomerCallLog.tsx's own onCallout exactly, with `enquiryId` in place of
            // `callLogId` -- the query param CalloutsPage.tsx now reads for either provenance.
            const params = new URLSearchParams({ new: "log", enquiryId: enquiry.id, customerId });
            navigate(`/office/callouts?${params.toString()}`);
          }}
          onPlannedWork={() => {
            setRaising(true);
            setAccepting(false);
          }}
          onQuote={() => {
            navigate("/office/quotes/new", {
              state: { prefill: { customerName: customerName ?? "" }, link: { customerId } },
            });
          }}
        />
      )}

      {raising && (
        <RaiseWorkModal
          preset={{ type: "repair", enquiryId: enquiry.id, customerId, customerName: customerName ?? undefined }}
          onClose={() => setRaising(false)}
          onRaised={(result) => {
            setRaising(false);
            setNotice(raisedWorkMessage(result));
            void onChanged();
          }}
        />
      )}
    </section>
  );
}

// Session ATM, Part D: proposed structured writes derived from the enquiry's own text (a new
// contact, a changed site access note, a price/invoicing confirmation to record as a note), held
// for office approval before anything is written. Runs only once the enquiry has a matched
// customer (the extraction itself requires one, per the backend spec); this panel disables the
// "Check for updates" button and explains why otherwise, rather than hiding it.
function SuggestedUpdatesSection({ enquiry }: { enquiry: EnquiryDetail }) {
  const [proposals, setProposals] = useState<EnquiryFactProposal[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.enquiries.facts.list(enquiry.id);
      setProposals(res.proposals);
    } catch (err) {
      setLoadError(friendlyApiError(err, "Could not load suggested updates for this enquiry."));
    } finally {
      setLoading(false);
    }
  }, [enquiry.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function generate() {
    setGenerating(true);
    setActionError(null);
    try {
      const res = await api.enquiries.facts.propose(enquiry.id);
      setProposals(res.proposals);
    } catch (err) {
      setActionError(friendlyApiError(err, "Could not check for updates. Please try again."));
    } finally {
      setGenerating(false);
    }
  }

  async function approve(factId: string) {
    setBusyId(factId);
    setActionError(null);
    try {
      const res = await api.enquiries.facts.approve(enquiry.id, factId);
      setProposals((list) => (list ? list.map((p) => (p.id === factId ? res.proposal : p)) : list));
    } catch (err) {
      setActionError(friendlyApiError(err, "Could not approve this update. Please try again."));
    } finally {
      setBusyId(null);
    }
  }

  async function dismiss(factId: string) {
    setBusyId(factId);
    setActionError(null);
    try {
      const res = await api.enquiries.facts.dismiss(enquiry.id, factId);
      setProposals((list) => (list ? list.map((p) => (p.id === factId ? res.proposal : p)) : list));
    } catch (err) {
      setActionError(friendlyApiError(err, "Could not dismiss this update. Please try again."));
    } finally {
      setBusyId(null);
    }
  }

  const pending = (proposals ?? []).filter((p) => p.status === "pending");
  const decided = (proposals ?? []).filter((p) => p.status !== "pending");

  return (
    <section className={sectionCls} aria-label="Suggested updates">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg text-ele-strong">Suggested updates</h2>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void generate()}
          disabled={generating || !enquiry.customerId}
          title={!enquiry.customerId ? "Match this enquiry to a customer first." : undefined}
        >
          {generating ? "Checking..." : "Check for updates"}
        </Button>
      </div>
      <p className="mb-3 text-xs text-ele-body opacity-80">
        {enquiry.customerId
          ? "Contact changes, site access notes or price/invoicing confirmations the AI has spotted in this conversation, for you to approve or dismiss."
          : "Match this enquiry to a customer above before checking for updates."}
      </p>

      {loadError ? (
        <p role="alert" className="text-sm text-ele-danger">
          {loadError}
        </p>
      ) : loading ? (
        <Loader shape="inline" />
      ) : (
        <>
          {actionError && (
            <p role="alert" className="mb-3 text-sm text-ele-danger">
              {actionError}
            </p>
          )}
          {pending.length === 0 ? (
            <p className="text-sm text-ele-body">No suggested updates right now.</p>
          ) : (
            <ul className="space-y-2">
              {pending.map((p) => (
                <li
                  key={p.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-ele border border-ele-hairline bg-ele-inset px-3 py-2"
                >
                  <div>
                    <span className="mr-2 font-menu text-[0.6rem] uppercase tracking-wide text-ele-body opacity-70">
                      {FACT_TYPE_LABEL[p.factType]}
                    </span>
                    <span className="text-sm text-ele-strong">{p.summary}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="primary" disabled={busyId === p.id} onClick={() => void approve(p.id)}>
                      {busyId === p.id ? "..." : "Approve"}
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busyId === p.id} onClick={() => void dismiss(p.id)}>
                      Dismiss
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {decided.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong">{decided.length} decided</summary>
              <ul className="mt-2 space-y-1">
                {decided.map((p) => (
                  <li key={p.id} className="text-xs text-ele-body opacity-70">
                    {FACT_TYPE_LABEL[p.factType]}: {p.summary} ({p.status})
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </section>
  );
}

function ActivitySection({ entityId }: { entityId: string }) {
  const [entries, setEntries] = useState<AuditTimelineEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    api.auditLog
      .list("enquiry", entityId)
      .then((res) => {
        if (live) setEntries(res.entries);
      })
      .catch((err) => {
        if (live) setError(friendlyApiError(err, "Could not load the activity for this record."));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [entityId]);

  return <AuditTimeline entries={entries} loading={loading} error={error} />;
}

function DeleteSection({ enquiry, onDeleted }: { enquiry: EnquiryDetail; onDeleted: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.enquiries.remove(enquiry.id);
      onDeleted();
    } catch (err) {
      // The server blocks this (409) once the enquiry has been sent or has linked work, and that
      // sentence is exactly what friendlyApiError surfaces -- never the raw 409/code.
      setError(friendlyApiError(err, "Could not delete this enquiry."));
      setBusy(false);
    }
  }

  return (
    <section className="rounded-ele border border-ele-hairline bg-ele-surface-soft p-5">
      <Button
        variant="destructive"
        size="sm"
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        Delete enquiry
      </Button>
      <ConfirmDeleteModal
        open={open}
        variant="destructive"
        title="Delete this enquiry?"
        message="This removes the enquiry from the review queue. This cannot be undone."
        confirmLabel="Delete enquiry"
        busy={busy}
        error={error}
        onConfirm={confirm}
        onCancel={() => {
          if (!busy) {
            setOpen(false);
            setError(null);
          }
        }}
      />
    </section>
  );
}
